import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const toCSV = (rows) =>
  rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

export function exportAccountGeneralReportToCSV({
  filename = "account_general_report.csv",
  balances = [],
  transactions = [],
  transfers = [],
}) {
  const lines = [];
  lines.push("Account Balances");
  lines.push(toCSV([["Account", "Currency", "Opening Balance", "Current Balance"]]));
  lines.push(
    toCSV(
      balances.map((b) => [
        b.name,
        b.currency_title,
        b.opening_balance,
        b.balance,
      ])
    )
  );
  lines.push("");
  lines.push("Account Transactions");
  lines.push(
    toCSV([
      [
        "Date",
        "Account",
        "Type",
        "Amount",
        "Transaction Currency",
        "Converted Amount",
        "Account Currency",
        "Description",
      ],
    ])
  );
  lines.push(
    toCSV(
      transactions.map((t) => [
        t.transaction_date,
        t.account_name,
        t.transaction_type,
        t.amount,
        t.transaction_currency_title,
        t.converted_amount,
        t.account_currency_title,
        t.description || "",
      ])
    )
  );
  lines.push("");
  lines.push("Account Transfers");
  lines.push(
    toCSV([
      [
        "Date",
        "From Account",
        "To Account",
        "Amount",
        "From Currency",
        "Exchange Rate",
        "Converted Amount",
        "To Currency",
        "Reference",
        "Note",
      ],
    ])
  );
  lines.push(
    toCSV(
      transfers.map((t) => [
        t.transfer_date,
        t.from_account_name,
        t.to_account_name,
        t.amount,
        t.from_currency_title,
        t.exchange_rate,
        t.converted_amount,
        t.to_currency_title,
        t.reference || "",
        t.note || "",
      ])
    )
  );

  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportAccountGeneralReportToPDF({
  filename = "account_general_report.pdf",
  balances = [],
  transactions = [],
  transfers = [],
}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleString();

  const numberOrZero = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const fixed2 = (value) => numberOrZero(value).toFixed(2);
  const fixed6 = (value) => numberOrZero(value).toFixed(6);

  const drawHeader = () => {
    doc.setFontSize(15);
    doc.setTextColor(33, 33, 33);
    doc.text("Account General Report", pageWidth / 2, 10, { align: "center" });
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text(`Generated: ${generatedAt}`, 10, 16);
    doc.setDrawColor(220, 220, 220);
    doc.line(10, 19, pageWidth - 10, 19);
  };

  const drawFooter = () => {
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setDrawColor(220, 220, 220);
      doc.line(10, pageHeight - 10, pageWidth - 10, pageHeight - 10);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 10, pageHeight - 5, {
        align: "right",
      });
    }
  };

  drawHeader();

  autoTable(doc, {
    startY: 24,
    head: [["Account", "Currency", "Opening", "Balance"]],
    body: balances.map((b) => [
      b.name,
      b.currency_title,
      fixed2(b.opening_balance),
      fixed2(b.balance),
    ]),
    headStyles: { fillColor: [41, 98, 255], textColor: [255, 255, 255] },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 28 },
      2: { cellWidth: 32, halign: "right" },
      3: { cellWidth: 32, halign: "right" },
    },
    margin: { left: 10, right: 10 },
  });

  let y = doc.lastAutoTable.finalY + 6;
  if (y > pageHeight - 24) {
    doc.addPage();
    drawHeader();
    y = 24;
  }
  doc.setFontSize(11);
  doc.setTextColor(33, 33, 33);
  doc.text("Transactions", 10, y);
  autoTable(doc, {
    startY: y + 2,
    head: [["Date", "Account", "Type", "Amount", "Txn Cur", "Converted", "Acc Cur"]],
    body: transactions.map((t) => [
      t.transaction_date,
      t.account_name,
      t.transaction_type,
      fixed2(t.amount),
      t.transaction_currency_title,
      fixed2(t.converted_amount),
      t.account_currency_title,
    ]),
    headStyles: { fillColor: [0, 121, 107], textColor: [255, 255, 255] },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 78 },
      2: { cellWidth: 22 },
      3: { cellWidth: 25, halign: "right" },
      4: { cellWidth: 22 },
      5: { cellWidth: 27, halign: "right" },
      6: { cellWidth: 22 },
    },
    margin: { left: 10, right: 10 },
  });

  y = doc.lastAutoTable.finalY + 6;
  if (y > pageHeight - 24) {
    doc.addPage();
    drawHeader();
    y = 24;
  }
  doc.setFontSize(11);
  doc.setTextColor(33, 33, 33);
  doc.text("Transfers", 10, y);
  autoTable(doc, {
    startY: y + 2,
    head: [
      [
        "Date",
        "From Account",
        "To Account",
        "Amount",
        "From Cur",
        "Rate",
        "Converted",
        "To Cur",
      ],
    ],
    body: transfers.map((t) => [
      t.transfer_date,
      t.from_account_name,
      t.to_account_name,
      fixed2(t.amount),
      t.from_currency_title,
      fixed6(t.exchange_rate),
      fixed2(t.converted_amount),
      t.to_currency_title,
    ]),
    headStyles: { fillColor: [123, 31, 162], textColor: [255, 255, 255] },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 54 },
      2: { cellWidth: 54 },
      3: { cellWidth: 22, halign: "right" },
      4: { cellWidth: 20 },
      5: { cellWidth: 26, halign: "right" },
      6: { cellWidth: 24, halign: "right" },
      7: { cellWidth: 20 },
    },
    margin: { left: 10, right: 10 },
  });

  drawFooter();
  doc.save(filename);
}
